import axios from "axios";
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

import {
  clearAllTokens,
  createBasicAuthorizationHeader,
  getCurrentAccessToken,
  getRefreshToken,
  getUserAccessTokenExpiresAt,
  setRefreshToken,
  setUserAccessToken,
  setUserAccessTokenExpiresAt,
} from "./utils/tokenStorage";
import { currentFeatures } from "@/configs/Settings";

/**
 * Erro padronizado do HTTP client (MVP)
 *
 * Objetivo:
 * - Evitar espalhar `AxiosError` pelo código.
 * - Deixar o tratamento de erro mais previsível para as aplicações.
 */
export interface HttpError {
  status?: number;
  message: string;
  code?: string;
  data?: unknown;
}

/**
 * Configuração estendida para as helpers HTTP.
 *
 * - Permite pular a lógica do interceptor de AUTH em chamadas específicas.
 * - Continua aceitando qualquer opção padrão do AxiosRequestConfig.
 *
 * Paridade com o legado (`useAxios` + `PostQuestionnaireResponse`):
 * - `withCredentials` pode ser definido por requisição; a instância usa `true` por padrão.
 * - O client **ehr-poster** (URL absoluta cross-origin em dev) envia `withCredentials: false`
 *   via `buildEhrPosterHttpConfig`, mantendo auth em `Authorization` / `x-user-token` como no src.
 */
export interface HttpRequestConfig extends AxiosRequestConfig {
  /**
   * Quando true, o interceptor de request NÃO vai injetar
   * Authorization (Bearer ...) nem aplicar lógica acoplada a AUTH.
   *
   * Útil para chamadas especiais como obtenção de tokens OAuth
   * usando Basic Auth, sem interferir nas demais requisições.
   */
  skipAuthInterceptors?: boolean;
}

export function normalizeHttpError(error: unknown): HttpError {
  const axiosError = error as AxiosError;

  const status = axiosError.response?.status;
  const data = axiosError.response?.data;

  return {
    status,
    code: axiosError.code,
    message:
      axiosError.message ||
      (status ? `Erro HTTP ${status}` : "Erro desconhecido"),
    data,
  };
}

/**
 * Instância axios compartilhada no `src-next`.
 *
 * Responsabilidades:
 * - Padronizar baseURL, timeout e headers.
 * - Injetar Authorization quando houver access token.
 * - Centralizar tratamentos comuns (ex.: retry em timeout e renovação de token).
 *
 * Importante:
 * - O fluxo de refresh token é simples e focado em segurança:
 *   - Renova pouco antes da expiração quando possível.
 *   - Se já expirou ou o servidor responder 401, limpa os tokens ("desloga").
 */

const baseUrl =
  import.meta.env.VITE_ENDPOINT_XDSB_domain ?? "https://dev.fluxmed.com.br";
const TOKEN_PATH = "/oauth2/token";

/**
 * Janela de segurança (ms) para renovar o token de usuário
 * antes da expiração efetiva.
 *
 * Exemplo: 60000 ms = 60 segundos.
 */
const USER_TOKEN_REFRESH_SAFETY_MS = 60000;

/**
 * Tipo mínimo esperado da resposta do endpoint OAuth.
 *
 * Mantido localmente para não acoplar `httpClient` à factory de auth.
 */
interface OAuthTokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
}

/**
 * Controle simples para evitar múltiplas chamadas de refresh
 * concorrentes. Quando um refresh está em andamento, as demais
 * requisições aguardam a mesma Promise.
 */
let ongoingUserTokenRefresh: Promise<void> | null = null;

export const httpClient: AxiosInstance = axios.create({
  baseURL: baseUrl,
  headers: {
    Accept: "*/*",
    "Content-Type": "application/json",
    "X-Target-System": currentFeatures.defaultApp ? undefined : "vet",
  },
  timeout: 60000,
  withCredentials: true,
  xsrfCookieName: "XSRF-TOKEN",
  xsrfHeaderName: "X-XSRF-TOKEN",
});

httpClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const extendedConfig = config as InternalAxiosRequestConfig & {
      skipAuthInterceptors?: boolean;
    };

    // Permite que chamadas específicas pulem a lógica de AUTH
    // (incluindo renovação automática e injeção de Bearer).
    if (extendedConfig.skipAuthInterceptors) {
      return extendedConfig;
    }

    /**
     * Renovação automática simples do token de USUÁRIO.
     *
     * Regras:
     * - Se não houver refresh_token ou informação de expiração, não faz nada.
     * - Se o token já estiver expirado, limpa os tokens (sessão expirada).
     * - Se estiver próximo de expirar (janela de segurança), tenta renovar.
     */
    const expiresAt = getUserAccessTokenExpiresAt();
    const refreshToken = getRefreshToken();

    if (expiresAt && refreshToken) {
      const now = Date.now();

      if (now >= expiresAt) {
        // Token já expirou: por segurança, limpamos tudo.
        clearAllTokens();
      } else if (expiresAt - now <= USER_TOKEN_REFRESH_SAFETY_MS) {
        // Próximo da expiração: tenta renovar uma única vez.
        if (!ongoingUserTokenRefresh) {
          ongoingUserTokenRefresh = (async () => {
            try {
              const body = new URLSearchParams();
              body.set("grant_type", "refresh_token");
              body.set("refresh_token", refreshToken);

              const basicAuth = createBasicAuthorizationHeader({
                consumerKey: "",
                consumerSecret: "",
              });

              const refreshConfig: HttpRequestConfig = {
                skipAuthInterceptors: true,
                timeout: 20000,
                headers: {
                  Accept: "*/*",
                  Authorization: basicAuth,
                  "Content-Type": "application/x-www-form-urlencoded",
                },
              };

              const response = await httpClient.post<OAuthTokenResponse>(
                TOKEN_PATH,
                body.toString(),
                refreshConfig,
              );

              // Atualiza tokens e nova expiração.
              const { access_token, refresh_token, expires_in } = response.data;

              setUserAccessToken(access_token);
              if (refresh_token) {
                setRefreshToken(refresh_token);
              }

              const newExpiresIn = expires_in;
              if (newExpiresIn && newExpiresIn > 0) {
                setUserAccessTokenExpiresAt(Date.now() + newExpiresIn * 1000);
              } else {
                setUserAccessTokenExpiresAt(null);
              }
            } catch {
              // Qualquer falha na renovação implica sessão inválida.
              clearAllTokens();
            } finally {
              ongoingUserTokenRefresh = null;
            }
          })();
        }

        await ongoingUserTokenRefresh;
      }
    }

    const token = getCurrentAccessToken();

    // Padroniza Authorization sem acoplar em contextos específicos.
    if (token) {
      // Headers em InternalAxiosRequestConfig são do tipo AxiosHeaders,
      // por isso usamos type assertion para evitar conflitos de tipagem.
      (extendedConfig.headers as any).Authorization = `Bearer ${token}`;
    }

    return extendedConfig;
  },
  (error) => Promise.reject(error),
);

httpClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const config = error.config as
      | (InternalAxiosRequestConfig & {
          _retry?: boolean;
        })
      | null;

    /**
     * Retry simples para timeouts
     *
     * - Inspirado na versão legada (`src/utils/useAxios.tsx`).
     * - Mantemos comportamento mínimo: 1 retry com timeout maior.
     * - Evitamos loops com a flag `_retry`.
     */
    if (error.code === "ECONNABORTED" && config && !config._retry) {
      config._retry = true;
      config.timeout = Math.round((config.timeout ?? 60000) * 1.5);
      return httpClient(config);
    }

    /**
     * Tratamento simples de 401:
     *
     * - Qualquer 401 em chamadas que usam Authorization indica sessão inválida.
     * - Por segurança, limpamos todos os tokens ("deslogar").
     * - Não tentamos novo refresh aqui para evitar loops.
     */
    if (error.response?.status === 401) {
      clearAllTokens();
    }

    return Promise.reject(normalizeHttpError(error));
  },
);

/**
 * Helpers tipados
 *
 * Objetivo:
 * - Fazer o restante do código trabalhar com `Promise<T>`,
 *   sem precisar acessar `.data` sempre.
 * - Padronizar retorno e erros.
 */
export async function httpGet<T>(
  url: string,
  config?: HttpRequestConfig,
): Promise<T> {
  const res = await httpClient.get<T>(url, config);
  return res.data;
}

export async function httpPost<TResponse, TBody = unknown>(
  url: string,
  body?: TBody,
  config?: HttpRequestConfig,
): Promise<TResponse> {
  const res = await httpClient.post<TResponse>(url, body, config);
  return res.data;
}

export async function httpPut<TResponse, TBody = unknown>(
  url: string,
  body?: TBody,
  config?: HttpRequestConfig,
): Promise<TResponse> {
  const res = await httpClient.put<TResponse>(url, body, config);
  return res.data;
}

export async function httpPatch<TResponse, TBody = unknown>(
  url: string,
  body?: TBody,
  config?: HttpRequestConfig,
): Promise<TResponse> {
  const res = await httpClient.patch<TResponse>(url, body, config);
  return res.data;
}

export async function httpDelete<T>(
  url: string,
  config?: HttpRequestConfig,
): Promise<T> {
  const res = await httpClient.delete<T>(url, config);
  return res.data;
}
